import { Metamodel } from '../base/model';
import { IModel, IAttribute } from '../base/annotation';
import { LinkedService } from '../linkedservice/linkedservice';

import { AzureKeyVault } from './key-vault';
import { validateAKVLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { KeyValuePairWithAkv } from 'shell-models-lib';
import { extractRelativeUrlFromFullUrl } from './web-url-uploaded-source.util';
import { extractDsrPathFromRawJson } from 'app/utils/powerqueryDsrUtils';

export const httpAuthTypes = {
    Anonymous: 'Anonymous',
    Basic: 'Basic',
    Digest: 'Digest',
    Windows: 'Windows',
    ClientCertificate: 'ClientCertificate'
};

export const httpCertificateLocationTypes = {
    EmbeddedCertificateData: 'EmbeddedCertificateData',
    CertificateThumbprint: 'CertificateThumbprint'
};

@Metamodel.model({
    name: adfModelTypeMap.httpServer.linkedServiceType,
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.HttpServer_DisplayText),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.HttpServer_DisplayText)
    ]
})
export class HttpServerLinkedService extends LinkedService {

    public static readonly MODEL: IModel;

    public static readonly BASE_URL: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly CERTIFICATE_THUMBPRINT: IAttribute;
    public static readonly AUTH_HEADER: IAttribute;

    @Metamodel.attribute({
        display: stringResources.ModelName_BaseUrl,
        association: 'BASE_URL',
        templatable: true
    })
    public url?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_EnableServerCertificateValidation
    })
    public enableServerCertificateValidation?: boolean = true;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType
    })
    public authenticationType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public userName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        association: 'PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    public certificateLocationType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_CertificateThumbprint,
        association: 'CERTIFICATE_THUMBPRINT',
        templatable: true
    })
    public certThumbprint?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_EmbeddedCertificateData
    })
    public embeddedCertData?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthHeaders,
        association: 'AUTH_HEADER'
    })
    public authHeaders?: KeyValuePairWithAkv[] = [];

    /**
     * Transient (non-persisted) field holding the absolute URL that PQO returned for an
     * uploaded-source connection. The Trident connection picker peeks at this to seed
     * the dataset's `relativeUrl` so the user does not have to re-type it. Not annotated
     * with `@Metamodel.attribute`, so it is excluded from serialization.
     */
    private _uploadedFullUrlFromPqo?: string;

    /** Stash the absolute upload URL that PQO emitted. Called by `UnifiedPQConnectionService`. */
    public setUploadedFullUrlFromPqo(fullUrl: string | undefined): void {
        this._uploadedFullUrlFromPqo = fullUrl;
    }

    /** Non-destructive: signals this LS came from the PQ upload-commit path. */
    public hasUploadedFullUrlFromPqo(): boolean {
        return !!this._uploadedFullUrlFromPqo;
    }

    /**
     * One-shot: returns the relative URL derived from the stashed full URL and clears the
     * stashed value. The base used for subtraction is `this.url` when populated, otherwise
     * the `path` advertised by the linked service's stringified `datasourceReference`
     * (which Trident populates before the typed attributes are hydrated). Returns
     * `undefined` when nothing was stashed or the derivation produced no relative portion.
     */
    public consumeRelativeUrlFromUploadedFullUrl(): string | undefined {
        const full = this._uploadedFullUrlFromPqo;
        this._uploadedFullUrlFromPqo = undefined;
        const baseUrl = this.url ?? extractDsrPathFromRawJson(this.raw);
        return extractRelativeUrlFromFullUrl(full, baseUrl);
    }

    constructor(name?: string, description?: string) {
        super(name, description);
    }
}
